import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

export type ToastOptions = {
  readonly retry?: boolean;
  readonly duration?: number;
};

export type Toast = {
  readonly id: string;
  readonly message: string;
  readonly severity: ToastSeverity;
  readonly options: ToastOptions;
};

const DEFAULT_DURATION = 5000;

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly toastsSubject = new BehaviorSubject<ReadonlyArray<Toast>>([]);
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  readonly toasts$: Observable<ReadonlyArray<Toast>> = this.toastsSubject.asObservable();

  show(message: string, severity: ToastSeverity, options?: ToastOptions): void {
    const id = crypto.randomUUID();
    const toast: Toast = {
      id,
      message,
      severity,
      options: {
        retry: options?.retry ?? false,
        duration: options?.duration ?? DEFAULT_DURATION,
      },
    };

    this.toastsSubject.next([...this.toastsSubject.getValue(), toast]);

    const duration = toast.options.duration ?? DEFAULT_DURATION;
    const timer = setTimeout(() => this.dismiss(id), duration);
    this.timers.set(id, timer);
  }

  dismiss(id: string): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    const current = this.toastsSubject.getValue();
    this.toastsSubject.next(current.filter((t) => t.id !== id));
  }
}
